import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getUserSync, getSync, claim, claimClick, claimDailyReward } from '@/services'
import { useUserStore } from '@/stores/user'
import { useConstantStore } from '@/stores/constant'

const usdFormatter = new Intl.NumberFormat('en-US', {
  currencyDisplay: 'code',
  maximumFractionDigits: 0
})

export const useGameStore = defineStore('game', () => {
  const constantStore = useConstantStore()
  const creditPerHour = ref(0)
  const lastClaim = ref('')
  const tokenPerHour = ref(0)
  const totalToken = ref(0)
  const calculatedTotalToken = ref(0)
  const level = ref(1)
  const lastDailyRewardClaimed = ref(false)
  const lastDailyRewardDay = ref(0)
  const lastDailySecretClaimed = ref(false)

  const levelNames = {
    1: 'Swabbie',
    2: 'Cabin Boy',
    3: 'Deckhand',
    4: 'Corsair',
    5: 'Gunner',
    6: 'Navigator',
    7: 'Quartermaster',
    8: 'First Mate',
    9: 'Captain',
    10: 'Dread Pirate'
  }

  const totalTokenPrintable = computed(() => usdFormatter.format(totalToken.value + calculatedTotalToken.value))
  const maxClicks = ref(0)
  const remainingClicks = ref(0)

  const activeCharacter = ref({})
  const activeCharacterId = ref(1)
  const activeIslandSequence = ref(1)

  const language = ref('en')
  const userCards = ref([])
  const userCharacters = ref([])
  const userSkins = ref([])
  const userTasks = ref([])

  const activeSkins = ref({
    sword: 0,
    hat: 0,
    shirts: 0,
    pants: 0,
    shoes: 0,
    belt: 0
  })

  let calculateInterval = null
  let claimInterval = null
  let clickCalculateInterval = null
  let waitingClicks = 0
  let lastClickSync = new Date()
  let lastClickLimitsUpdated = new Date()

  const getLevelName = () => levelNames[level.value]

  const fetchUserSync = () => {
    return new Promise(async (resolve) => {
      const res = await getUserSync()
      console.log('USERSYNC RESPONSE: ', res)
      language.value = res.data.language
      activeCharacter.value = res.data.activeCharacter
      activeCharacterId.value = res.data.activeCharacter.character_id
      activeIslandSequence.value = res.data.activeIslandSequence
      lastDailyRewardClaimed.value = res.data.last_daily_reward_claimed
      lastDailyRewardDay.value = res.data.last_daily_reward_day
      lastDailySecretClaimed.value = res.data.last_daily_secret_claimed
      remainingClicks.value = res.data.remaining_clicks

      userCards.value = res.data.cards
      res.data.cards.forEach((element) => {
        const i = constantStore.cards.findIndex((c) => c.id === element.card_id)

        constantStore.cards.splice(i, 1, {
          ...constantStore.cards[i],
          level: element.level,
          token_per_hour: element.token_per_hour,
          next_token_per_hour: element.next_token_per_hour,
          upgrade_price: element.upgrade_price
        })
      })

      userCharacters.value = res.data.characters
      res.data.characters.forEach((element) => {
        const i = constantStore.characters.findIndex((c) => c.id === element.character_id)
        constantStore.characters.splice(i, 1, {
          ...constantStore.characters[i],
          isUsed: res.data.activeCharacter.character_id === element.character_id,
          isOwned: true,
          userCharacterId: element.id
        })
      })

      userSkins.value = res.data.skins
      res.data.skins.forEach((element) => {
        const i = constantStore.skins.findIndex((c) => c.id === element.skin_id)
        const isUsed = element.skin_id == activeCharacter.value[constantStore.skins[i].type + '_id']
        constantStore.skins.splice(i, 1, {
          ...constantStore.skins[i],
          buy_price: element.upgrade_price,
          level: element.level,
          isOwned: true,
          isUsed: isUsed,
          userSkinId: element.id
        })

        if (isUsed) {
          activeSkins.value[constantStore.skins[i].type] = constantStore.skins[i]
        }
      })

      res.data.tasks.forEach((element) => {
        const cTask = constantStore.tasks.find((c) => c.id === element.taskId)
        const i = constantStore.islands.findIndex((c) => c.id === cTask.island_id)
        const j = constantStore.islands[i].tasks.findIndex((c) => c.id === element.taskId)
        constantStore.islands[i].tasks[j].isCompleted = element.isCompleted
        constantStore.islands[i].tasks[j].claimedAt = element.claimedAt
      })

      constantStore.islands.forEach((element, i) => {
        constantStore.islands[i].isCompleted = element.tasks
          ? element.tasks.every((x) => x.isCompleted)
          : false

        constantStore.islands[i].isLocked = element.sequence > activeIslandSequence.value
      })

      setTimeout(() => {
        resolve(true)
      }, 500)
    })
  }

  const fetchSync = async () => {
    const res = await getSync()
    console.log('SYNC RESPONSE: ', res)
    creditPerHour.value = res.data.credit_per_hour
    lastClaim.value = Date.parse(res.data.last_claim_at) / 1000
    tokenPerHour.value = res.data.token_per_hour
    calculatedTotalToken.value = 0
    totalToken.value = res.data.total_token
    return true
  }

  const claimDaily = async () => {
    claimDailyReward()
  }

  const clickCharacter = async () => {
    calculatedTotalToken.value += 1
    remainingClicks.value -= 1
    waitingClicks += 1
  }

  const getActiveCharacter = () =>
    computed(() => constantStore.getCharacter(activeCharacterId.value))

  const startCalculateInterval = () => {
    calculateInterval = setInterval(() => {
      const t = Date.now()
      const now = Math.floor(t / 1000)
      const threeHourSecond = 3 * 60 * 60
      const leftSec = now - lastClaim.value
      if (leftSec >= threeHourSecond) {
        return true
      }

      calculatedTotalToken.value = Math.ceil((leftSec * tokenPerHour.value) / 3600) + waitingClicks
    }, 3000)
  }

  const clearCalculateInterval = () => {
    clearInterval(calculateInterval)
  }

  const startClaimInterval = () => {
    claimInterval = setInterval(async () => {
      await claim()
      fetchSync()
    }, 60 * 1000)
  }

  const clearClaimInterval = () => {
    clearInterval(claimInterval)
  }

  const startClickCalculateInterval = () => {
    maxClicks.value = 100 * (level.value / 2)

    clickCalculateInterval = setInterval(async () => {
      if (new Date() - lastClickSync > 10_000 && waitingClicks > 0) {
        // sync on each 10 sec
        lastClickSync = new Date()
        const res = await claimClick({ clicks: waitingClicks })
        if (res.data['accepted_clicks'] != waitingClicks) {
          calculatedTotalToken.value += res.data['accepted_clicks'] - waitingClicks
        }
        waitingClicks = 0
      }

      const interval = new Date() - lastClickLimitsUpdated
      const clicks = Number.parseInt(((100 * (level.value / 2)) / 600_000) * interval)
      if (clicks > 0) {
        lastClickLimitsUpdated =
          lastClickLimitsUpdated + clicks / ((100 * (level.value / 2)) / 600_000)
        remainingClicks.value += Math.min(remainingClicks.value + clicks, 100 * (level.value / 2))
      }
    }, 5 * 1000)
  }

  const clearClickCalculateInterval = () => {
    clearInterval(clickCalculateInterval)
  }

  return {
    creditPerHour,
    lastClaim,
    tokenPerHour,
    totalToken,
    calculatedTotalToken,
    lastDailyRewardDay,
    lastDailyRewardClaimed,

    level,
    levelNames,
    getLevelName,
    totalTokenPrintable,
    maxClicks,
    remainingClicks,

    activeCharacterId,
    activeSkins,
    getActiveCharacter,
    fetchUserSync,
    fetchSync,
    clickCharacter,
    claimDaily,
    startCalculateInterval,
    clearCalculateInterval,
    startClaimInterval,
    clearClaimInterval,
    startClickCalculateInterval,
    clearClickCalculateInterval
  }
})
